import { useEffect, useState } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { toast } from "react-toastify";
import { usePermisos } from "@/hooks/usePermisos";
import { FaUserPlus, FaCog, FaUserShield } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import WizardUsuario from "../../components/usuarios/WizardUsuario";

export default function GestionUsuarios({ onClose }) {
    const { canCreate, canUpdate, canDelete } = usePermisos();
    const navigate = useNavigate();

    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showWizard, setShowWizard] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [includeDisabled, setIncludeDisabled] = useState(false);

    useEffect(() => {
        fetchUsuarios();
    }, [includeDisabled]);

    const fetchUsuarios = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_ROUTES.usuariosList}?includeDisabled=${includeDisabled}`);
            const usuariosData = Array.isArray(response.data) ? response.data : [];
            setUsuarios(usuariosData);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
            toast.error('Error al cargar usuarios');
            setUsuarios([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenWizard = (user = null) => {
        setEditingUser(user);
        setShowWizard(true);
    };

    const handleCloseWizard = (success = false) => {
        setShowWizard(false);
        setEditingUser(null);
        if (success) {
            fetchUsuarios();
        }
    };

    const handleToggleEnabled = async (userId) => {
        try {
            const res = await axios.patch(API_ROUTES.usuarioToggleEnabled(userId));
            toast.success(res.data.message);
            fetchUsuarios();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            toast.error('Error al cambiar estado del usuario');
        }
    };

    const handleDelete = async (userId) => {
        if (!confirm('¿Estás seguro de que deseas deshabilitar este usuario?')) return;

        try {
            await axios.delete(API_ROUTES.usuarioDelete(userId));
            toast.success('Usuario deshabilitado correctamente');
            fetchUsuarios();
        } catch (error) {
            console.error('Error al eliminar usuario:', error);
            toast.error('Error al deshabilitar usuario');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 p-4">
            <div className="max-w-7xl mx-auto">
                {/* Header mejorado */}
                <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl shadow-xl p-6 mb-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                                <FaUserShield className="text-4xl" />
                                Gestión de Usuarios
                            </h1>
                            <p className="text-cyan-100 mt-2">
                                Administra los usuarios del sistema y sus permisos
                            </p>
                        </div>
                        <div className="flex gap-3">
                            {/* Botón para ir a gestión de perfiles */}
                            <button
                                onClick={() => navigate('/admin/perfiles')}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                                title="Gestionar perfiles de permisos"
                            >
                                <FaCog /> Gestionar Perfiles
                            </button>

                            {/* Botón para crear usuario */}
                            {canCreate('usuarios') && (
                                <button
                                    onClick={() => handleOpenWizard()}
                                    className="bg-white text-cyan-600 px-6 py-3 rounded-lg font-semibold hover:bg-cyan-50 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
                                >
                                    <FaUserPlus /> Nuevo Usuario
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeDisabled}
                            onChange={(e) => setIncludeDisabled(e.target.checked)}
                            className="w-5 h-5 text-cyan-600 rounded focus:ring-cyan-500"
                        />
                        <span className="font-medium text-gray-700">
                            Incluir usuarios deshabilitados
                        </span>
                    </label>
                </div>

                {/* Tabla de usuarios */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Usuario
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Nombre
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Empresa
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Perfil
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center">
                                            <div className="flex justify-center items-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                                                <span className="ml-3 text-gray-600">Cargando usuarios...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : usuarios.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            No hay usuarios registrados
                                        </td>
                                    </tr>
                                ) : (
                                    usuarios.map((usuario) => (
                                        <tr key={usuario.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {usuario.usuario}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-700">{usuario.nombre}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-700">{usuario.empresa || 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                                    <FaUserShield />
                                                    {usuario.perfil_nombre || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${usuario.enabled === 1
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {usuario.enabled === 1 ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                                {canUpdate('usuarios') && (
                                                    <>
                                                        <button
                                                            onClick={() => handleOpenWizard(usuario)}
                                                            className="text-blue-600 hover:text-blue-900 transition font-medium"
                                                        >
                                                            Editar
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleEnabled(usuario.id)}
                                                            className="text-yellow-600 hover:text-yellow-900 transition font-medium"
                                                        >
                                                            {usuario.enabled === 1 ? 'Deshabilitar' : 'Habilitar'}
                                                        </button>
                                                    </>
                                                )}
                                                {canDelete('usuarios') && usuario.enabled === 1 && (
                                                    <button
                                                        onClick={() => handleDelete(usuario.id)}
                                                        className="text-red-600 hover:text-red-900 transition font-medium"
                                                    >
                                                        Eliminar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Stats rápidos */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="text-3xl font-bold text-cyan-600">
                            {usuarios.filter(u => u.enabled === 1).length}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Usuarios Activos</div>
                    </div>
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="text-3xl font-bold text-red-600">
                            {usuarios.filter(u => u.enabled === 0).length}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Usuarios Inactivos</div>
                    </div>
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <div className="text-3xl font-bold text-blue-600">
                            {usuarios.length}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">Total de Usuarios</div>
                    </div>
                </div>
            </div>

            {/* Wizard de creación/edición */}
            {showWizard && (
                <WizardUsuario
                    onClose={handleCloseWizard}
                    usuarioEditar={editingUser}
                />
            )}
        </div>
    );
}